/**
 * Richiesta guidata («La Cartolina»): identificativi stabili di intenti, domande
 * e opzioni.
 *
 * Nello stato vivono SOLO questi id (più gli slug del catalogo e il testo che
 * l'utente digita): le etichette si leggono dall'i18n al momento di mostrarle o
 * di comporre il messaggio, così un cambio di lingua ricompone tutto.
 */

export const INTENTS = ["offer", "custom", "branding", "advice", "order", "question", "other"] as const;
export type IntentId = (typeof INTENTS)[number];

/** Le tessere del passo 1: «Altro» non è una tessera, lo imposta «Preferisco scrivere». */
export const TILE_INTENTS: readonly IntentId[] = ["offer", "custom", "branding", "advice", "order", "question"];

export const QTY_BANDS = ["q18_50", "q51_100", "q101_250", "q251_500", "q500plus", "qUnknown"] as const;
export type QtyBandId = (typeof QTY_BANDS)[number];

/** `christmas` compare solo in stagione (vedi season.ts) ed è sempre il primo. */
export const TIMINGS = ["christmas", "asap", "date", "exploring"] as const;
export type TimingId = (typeof TIMINGS)[number];

export const OCCASIONS = [
  "christmasYearEnd", "employees", "clients", "welcome", "birthdays", "farewell", "recurring", "occasionOther",
] as const;
export type OccasionId = (typeof OCCASIONS)[number];

/** Destinatari dichiarati dal sito (About, pagina fiscale): solo categorie, mai persone. */
export const RECIPIENTS = ["employees", "teams", "clients", "executives"] as const;
export type RecipientId = (typeof RECIPIENTS)[number];

/** Personalizzazioni già offerte sul sito (FAQ «logo, etichette personalizzate e biglietti dedicati»). */
export const BRANDING = ["logo", "label", "card"] as const;
export type BrandingId = (typeof BRANDING)[number];

export const ORDER_TOPICS = ["status", "invoice", "vat", "delivery", "payment", "orderOther"] as const;
export type OrderTopicId = (typeof ORDER_TOPICS)[number];

export const QUESTION_TOPICS = ["contents", "allergens"] as const;
export type QuestionTopicId = (typeof QUESTION_TOPICS)[number];

export type QuestionId =
  | "boxes" | "box" | "qtyBand" | "timing" | "occasion" | "recipients" | "branding" | "orderTopic" | "questionTopics";

/** Domande di ciascun intento, nell'ordine in cui compaiono al passo «Dettagli». */
export const INTENT_QUESTIONS: Record<IntentId, readonly QuestionId[]> = {
  offer: ["boxes", "qtyBand", "timing", "branding"],
  custom: ["boxes", "qtyBand", "timing", "branding"],
  branding: ["branding", "boxes", "qtyBand", "timing"],
  advice: ["occasion", "recipients", "qtyBand", "timing"],
  order: ["orderTopic"],
  question: ["box", "questionTopics"],
  other: [],
};

/** Intenti che mostrano le confezioni del catalogo (e quindi lo caricano). */
export const CATALOG_INTENTS: readonly IntentId[] = ["offer", "custom", "branding", "question"];

/**
 * Intenti senza passo «Dettagli»: l'argomento dell'ordine si sceglie in linea al
 * passo 1, «Altro» è solo testo. Per loro la richiesta è fatta di 2 passi.
 */
const NO_DETAILS: readonly IntentId[] = ["order", "other"];

export type StepId = 1 | 2 | 3;

/** Sequenza dei passi per l'intento scelto (prima della scelta se ne mostrano 3). */
export function stepsFor(intent: IntentId | null): readonly StepId[] {
  return intent && NO_DETAILS.includes(intent) ? [1, 3] : [1, 2, 3];
}

export function isIntentId(v: unknown): v is IntentId {
  return typeof v === "string" && (INTENTS as readonly string[]).includes(v);
}

/** Slug del catalogo: stessa forma che accetta il backend per le schede prodotto. */
export const SLUG_RE = /^[a-z0-9-]{1,64}$/;

/**
 * Slug utilizzabile come chiave: forma valida e nessun nome ereditato da
 * Object.prototype («constructor», «tostring»…), che leggerebbe una funzione.
 */
export function isSafeSlug(v: unknown): v is string {
  return typeof v === "string" && SLUG_RE.test(v) && !(v in Object.prototype);
}

/** Titolo da mostrare e da mettere nel messaggio: niente a capo né caratteri di controllo. */
export function cleanTitle(v: unknown, max = 120): string {
  if (typeof v !== "string") return "";
  const visible = Array.from(v, (ch) => {
    const c = ch.charCodeAt(0);
    return c < 32 || c === 127 ? " " : ch;
  }).join("");
  return visible.replace(/\s+/g, " ").trim().slice(0, max);
}
